// Copyright (C) Lembani Sakala - All rights reserved.
// Lembani Sakala.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../../../app/hooks';
import { RootState } from '../../../app/store';

interface NavItem {
  id: string;
  label: string;
  icon: string;
  path: string;
}

export const AdminLayout = ({ children }: { children: React.ReactNode }) => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo } = useAppSelector((state: RootState) => state.user);

  const navItems: NavItem[] = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊', path: '/admin/dashboard' },
    { id: 'enquiries', label: 'Enquiries', icon: '💬', path: '/admin/enquiries' },
    { id: 'bookings', label: 'Bookings', icon: '📅', path: '/admin/bookings' },
  ];

  const [activeRoute, setActiveRoute] = useState('dashboard');

  useEffect(() => {
    const path = location.pathname.toLowerCase();
    if (path.includes('enquiries')) setActiveRoute('enquiries');
    else if (path.includes('bookings')) setActiveRoute('bookings');
    else setActiveRoute('dashboard');
  }, [location.pathname]);

  const handleNavigation = (item: NavItem) => {
    setActiveRoute(item.id);
    navigate(item.path);
  };

  return (
    <div style={styles.container}>
      {/* Modern Sidebar */}
      <aside style={{
        ...styles.sidebar,
        width: isSidebarCollapsed ? '80px' : '280px',
      }}>
        {/* Header */}
        <div style={styles.sidebarHeader}>
          <div style={styles.brandContainer}>
            <div style={styles.brandIcon}>🚗</div>
            {!isSidebarCollapsed && (
              <div style={styles.brandText}>
                <h4 style={styles.brandTitle}>CityDrive</h4>
                <span style={styles.brandSubtitle}>Admin Panel</span>
              </div>
            )}
          </div>
          <button
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            style={styles.collapseBtn}
            title={isSidebarCollapsed ? 'Expand' : 'Collapse'}
          >
            {isSidebarCollapsed ? '→' : '←'}
          </button>
        </div>

        {/* User Profile */}
        <div style={styles.userProfile}>
          <div style={styles.userAvatar}>
            {userInfo?.firstname?.[0]}{userInfo?.lastname?.[0]}
          </div>
          {!isSidebarCollapsed && (
            <div style={styles.userInfo}>
              <div style={styles.userName}>
                {userInfo?.firstname} {userInfo?.lastname}
              </div>
              <div style={styles.userEmail}>{userInfo?.username}</div>
              <span style={styles.adminBadge}>Administrator</span>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav style={styles.nav}>
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleNavigation(item)}
              style={{
                ...styles.navItem,
                ...(activeRoute === item.id ? styles.navItemActive : {}),
              }}
              title={isSidebarCollapsed ? item.label : ''}
            >
              <span style={styles.navIcon}>{item.icon}</span>
              {!isSidebarCollapsed && (
                <span style={styles.navLabel}>{item.label}</span>
              )}
              {activeRoute === item.id && (
                <div style={styles.activeIndicator} />
              )}
            </button>
          ))}
        </nav>

        {/* Divider */}
        <div style={styles.divider} />

        {/* Back to Dashboard */}
        <button
          onClick={() => navigate('/dashboard/dashboard')}
          style={styles.backButton}
          title={isSidebarCollapsed ? 'User Dashboard' : ''}
        >
          <span style={styles.navIcon}>👤</span>
          {!isSidebarCollapsed && (
            <span style={styles.navLabel}>User Dashboard</span>
          )}
        </button>
      </aside>

      {/* Main Content */}
      <main style={styles.mainContent}>
        {/* Top Bar */}
        <div style={styles.topBar}>
          <div style={styles.breadcrumb}>
            <span style={styles.breadcrumbItem}>Admin</span>
            <span style={styles.breadcrumbSeparator}>/</span>
            <span style={styles.breadcrumbActive}>
              {navItems.find(item => item.id === activeRoute)?.label || 'Dashboard'}
            </span>
          </div>
          <div style={styles.topBarRight}>
            <div style={styles.dateTime}>
              {new Date().toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric'
              })}
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div style={styles.contentArea}>
          {children}
        </div>
      </main>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: '#f8f9fa',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  sidebar: {
    background: 'linear-gradient(180deg, #1a1f36 0%, #0f1419 100%)',
    color: '#fff',
    padding: '24px 16px',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '4px 0 24px rgba(0,0,0,0.12)',
    transition: 'width 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
  },
  sidebarHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px',
  },
  brandContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  brandIcon: {
    fontSize: '32px',
    filter: 'drop-shadow(0 2px 8px rgba(255,255,255,0.2))',
  },
  brandText: {
    display: 'flex',
    flexDirection: 'column',
  },
  brandTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '700',
    letterSpacing: '-0.5px',
  },
  brandSubtitle: {
    fontSize: '11px',
    color: '#8b92a7',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontWeight: '600',
  },
  collapseBtn: {
    background: 'rgba(255,255,255,0.1)',
    border: 'none',
    color: '#fff',
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
    fontSize: '16px',
  },
  userProfile: {
    background: 'rgba(255,255,255,0.05)',
    borderRadius: '16px',
    padding: '20px',
    marginBottom: '32px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255,255,255,0.1)',
  },
  userAvatar: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: '700',
    flexShrink: 0,
    boxShadow: '0 4px 12px rgba(102,126,234,0.4)',
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: 0,
  },
  userName: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#fff',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  userEmail: {
    fontSize: '12px',
    color: '#8b92a7',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  adminBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    borderRadius: '6px',
    fontSize: '10px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginTop: '4px',
    alignSelf: 'flex-start',
  },
  nav: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flex: 1,
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 16px',
    background: 'transparent',
    border: 'none',
    borderRadius: '12px',
    color: '#8b92a7',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontSize: '15px',
    fontWeight: '500',
    position: 'relative',
    textAlign: 'left',
  },
  navItemActive: {
    background: 'rgba(255,255,255,0.1)',
    color: '#fff',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  },
  navIcon: {
    fontSize: '20px',
    flexShrink: 0,
    filter: 'grayscale(0.3)',
  },
  navLabel: {
    flex: 1,
  },
  activeIndicator: {
    position: 'absolute',
    right: '16px',
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    boxShadow: '0 0 8px rgba(102,126,234,0.8)',
  },
  divider: {
    height: '1px',
    background: 'rgba(255,255,255,0.1)',
    margin: '24px 0',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 16px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '12px',
    color: '#8b92a7',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontSize: '15px',
    fontWeight: '500',
  },
  mainContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  topBar: {
    background: '#fff',
    padding: '20px 32px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    borderBottom: '1px solid #e9ecef',
  },
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
  },
  breadcrumbItem: {
    color: '#6c757d',
  },
  breadcrumbSeparator: {
    color: '#dee2e6',
  },
  breadcrumbActive: {
    color: '#212529',
    fontWeight: '600',
  },
  topBarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  dateTime: {
    fontSize: '14px',
    color: '#6c757d',
    fontWeight: '500',
  },
  contentArea: {
    flex: 1,
    padding: '32px',
    overflow: 'auto',
  },
};
